<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { copyToClipBoard, errorNotify, successNotify, prettifyDateTime } from "@/mixins";
import { useStore } from "vuex";
import { useLoading } from "vue-loading-overlay";
import { usePageHeader } from "@/composables/usePageHeader";
import OrganizationModal from "@/components/modals/new_locker/OrganizationModal.vue";
import AddressModal from "@/components/modals/new_locker/AddressModal.vue";
import ControlUnitModal from "@/components/modals/new_locker/ControlUnitModal.vue";
import InformationModal from "@/components/modals/new_locker/InformationModal.vue";
import CellModal from "@/components/modals/new_locker/CellModal.vue";
import CellServiceModal from "@/components/modals/CellServiceModal.vue";
import DataTable from "@/components/panels/DataTable.vue";

const store = useStore()
const router = useRouter()
const route = useRoute()
const $loading = useLoading({});
const { setPageTitle, setPageCrumbs } = usePageHeader()

const getInstanceError = computed(() => store.getters['locker/lockerDataError'])
const updateInstanceError = computed(() => store.getters['locker/lockerUpdateError'])
const instance = computed(() => store.getters['locker/lockerData'])
const getInstance = (val) => store.dispatch('locker/getLocker', val)
const updateInstance = (val) => store.dispatch('locker/updateLocker', val)

const currentUserData = computed(() => store.getters['user/currentUserData'])
const isAdmin = computed(() => currentUserData.value.role === 'administrator')
const canResolveCells = computed(() => ['administrator', 'manager'].includes(currentUserData.value.role))

const showOrganizationModal = ref(false)
const showInformationModal = ref(false)
const showControlUnitModal = ref(false)
const showAddressModal = ref(false)
const showCellModal = ref(false)
// CellModal also tracks its cell by number, like the out of service panel below
const editCellNumber = ref(null)
const editCell = computed(() => (editCellNumber.value === null ? null : locker.value.cells_list[editCellNumber.value - 1] || null))
// cells whose flags were changed in CellModal and not saved yet: refetches keep those flags
const dirtyCells = new Set()
// the out of service panel tracks a cell by number: refetches replace the cell objects
const serviceCellNumber = ref(null)

// statuses an admin may pick; offline/sleeping are set by the backend only and are shown
// (non-selectable) just to display a locker's current state
const lockerStatuses = ['online', 'maintenance', 'error']
const STATUS_SEL = { online: 'st-online', offline: 'st-offline', maintenance: 'st-maint', error: 'st-error', sleeping: 'st-offline' }

const locker = ref({
  organization: {},
  information: {
    sn: '',
    name: '',
    model: {},
    ip_address: '',
    wifi_ssid: '',
    wifi_password: '',
    cell_firmware_version: ''
  },
  control_unit: {
    unit: '',
    head_firmware_version: '',
  },
  address: {},
  cells_list: [],
  in_sync: false,
  battery_level: null,
  status: '',
})

function setOrganization(inst) { locker.value.organization = inst }
function setInformation(inst) { locker.value.information = inst }
function setControlUnit(inst) { locker.value.control_unit.sn = inst.sn }
function setAddress(inst) { locker.value.address = inst }
// only the flags are editable; the rest of the cell comes from the device
function setCell(inst) {
  const cell = locker.value.cells_list[inst.number - 1]
  if (!cell) return
  locker.value.cells_list[inst.number - 1] = {
    ...cell,
    is_broken: inst.is_broken,
    cleaning_required: inst.cleaning_required,
    is_active: inst.is_active,
  }
  dirtyCells.add(inst.number)
}

// ---- cell map: model's cells_map ({number: 'S'|'M'|'L'|'XL'}), each letter
// takes up a certain number of slots; a rack column holds 16 slots — once the
// accumulated sum reaches 16, a new column starts ----
const SIZE_SPAN = { S: 1, M: 2, L: 4, XL: 6 };
const spanOf = (size) => SIZE_SPAN[size] || 1;
const RACK_SLOTS = 16;

const racks = computed(() => {
  const map = locker.value.information.model?.cells_map || {};
  const nums = Object.keys(map).map(Number).sort((a, b) => a - b);
  const result = [];
  let current = [];
  let slots = 0;
  nums.forEach((n) => {
    const span = spanOf(map[n]);
    current.push({ n, size: map[n], span });
    slots += span;
    if (slots >= RACK_SLOTS) {
      result.push(current);
      current = [];
      slots = 0;
    }
  });
  if (current.length) result.push(current);
  return result;
});

// racks are grouped by 2 into a module (see .lkd-cmodule — a module takes up 1/3
// of the block width and doesn't stretch across the whole block)
const modules = computed(() => {
  const result = [];
  for (let i = 0; i < racks.value.length; i += 2) result.push(racks.value.slice(i, i + 2));
  return result;
});

// the width of the top mm ruler must match the actual width of the occupied
// modules (1/2/3 from .lkd-cmodule flex-basis), not the whole block.
// ruler-h is position:absolute inside .lkd-bp, so its "100%" is the full
// width of .lkd-bp (with padding), not the content width of .lkd-cmap, which
// is what the modules are actually computed from — compensate for .lkd-bp's padding (56+26=82px)
const BP_H_PADDING = 82;
const MODULE_GAP = 16;
const rulerWidth = computed(() => {
  const n = Math.min(modules.value.length, 3);
  if (n <= 0) return '0px';
  const pxSubtract = ((BP_H_PADDING + 2 * MODULE_GAP) * n) / 3 - (n - 1) * MODULE_GAP;
  return `calc(${n} / 3 * 100% - ${pxSubtract}px)`;
});

function cellState(n) {
  const cell = locker.value.cells_list[n - 1];
  if (!cell) return 'st-empty';
  // checked before is_active: an out of service cell is inactive too
  if (cell.status === 'out_of_service') return 'st-oos';
  if (cell.is_broken) return 'st-broken';
  if (!cell.is_active) return 'st-empty';
  if (['occupied', 'rented'].includes(cell.status)) return 'st-occupied';
  if (cell.status === 'reserved') return 'st-reserved';
  return 'st-active';
}

const cellOf = (n) => locker.value.cells_list[n - 1];

function cellTitle(c) {
  const cell = cellOf(c.n);
  const parts = [`Cell ${c.n}`, c.size];
  if (!cell) parts.push('not registered by the device yet');
  else {
    if (cell.status === 'out_of_service') parts.push('out of service');
    if (cell.board_confirmed === false) parts.push('awaiting new board');
  }
  return parts.join(' · ');
}

// ---- top stats: computed from each cell's actual status ----
const totalCells = computed(() =>
  locker.value.information.model?.total_cells
  ?? Object.keys(locker.value.information.model?.cells_map || {}).length);

const cellCounts = computed(() => {
  const map = locker.value.information.model?.cells_map || {};
  const counts = { active: 0, occupied: 0, broken: 0, reserved: 0 };
  Object.keys(map).map(Number).forEach((n) => {
    const state = cellState(n);
    if (state === 'st-active') counts.active += 1;
    else if (state === 'st-occupied') counts.occupied += 1;
    else if (state === 'st-broken') counts.broken += 1;
    else if (state === 'st-reserved') counts.reserved += 1;
  });
  return counts;
});

const cellMetrics = computed(() => [
  { label: 'Active', value: cellCounts.value.active, icon: 'shield-check-outline', color: 'var(--lb-success)' },
  { label: 'Occupied', value: cellCounts.value.occupied, icon: 'package-variant-closed', color: 'var(--lb-blue)' },
  { label: 'Broken', value: cellCounts.value.broken, icon: 'link-variant-off', color: 'var(--lb-red)' },
  { label: 'Reserved', value: cellCounts.value.reserved, icon: 'bookmark-outline', color: 'var(--lb-warning)' },
]);

// TODO(cell occupancy): no backend endpoint for per-day cell-occupancy
// history yet — using demo data for now, swap for real data once the API exists.
const occupancyDemo = [
  { day: 'Mon', value: 3 }, { day: 'Tue', value: 2 }, { day: 'Wed', value: 6 },
  { day: 'Thu', value: 7 }, { day: 'Fri', value: 2 }, { day: 'Sat', value: 8 },
  { day: 'Sun', value: 5 },
];
const occupancyMax = Math.max(...occupancyDemo.map((d) => d.value));
const occupancyToday = computed(() => {
  const total = totalCells.value || 1;
  const last = occupancyDemo[occupancyDemo.length - 1].value;
  return Math.round((last / total) * 100);
});

// ---- Recent Activity: orders on this locker ----
const orderList = computed(() => store.getters['order/orderList']);
const recentOrders = computed(() => orderList.value?.results || []);

const ORDER_STATUS_META = {
  created: { pill: 'created', label: 'Created' },
  active: { pill: 'active', label: 'Active' },
  finished: { pill: 'finished', label: 'Finished' },
  expired: { pill: 'expired', label: 'Expired' },
};
const orderCols = 'minmax(120px,0.9fr) 90px 130px 150px 150px';
const orderColumns = ['Order ID', 'Cell', 'Status', 'Created', 'Expired'];
const manualCols = 'minmax(120px,1fr) 90px 170px 130px';
const manualColumns = ['Order ID', 'Cell', 'Locked', ''];

function loadRecentOrders() {
  store.dispatch('order/getOrderList', `?device=${route.params.slug}&page_size=8`);
}

function goToOrder(o) {
  router.push({ name: 'order-detail', params: { id: o.id } });
}

const LEGEND = [
  ['linear-gradient(160deg, #6FCB74, #4CAF50)', 'Active'],
  ['linear-gradient(160deg, #EF6A66, #E21D26)', 'Broken'],
  ['linear-gradient(160deg, #C62828, #8E0F14)', 'Out of service'],
  ['linear-gradient(160deg, #4F7BEA, #0040DD)', 'Occupied'],
  ['linear-gradient(160deg, #FFC966, #FB8C00)', 'Reserved'],
];

// ---- out of service cells: orders left in them wait for a manual resolution ----
const manualOrderList = computed(() => store.getters['order/manualOrderList'])
const manualOrders = computed(() => manualOrderList.value?.results || [])
// manual_processing stays true after resolving, so "unresolved" is filtered here
// (see docs/backend-questions.md)
const unresolvedOrders = computed(() => manualOrders.value.filter((o) => !o.resolution))
const serviceCell = computed(() =>
  serviceCellNumber.value === null ? null : locker.value.cells_list[serviceCellNumber.value - 1] || null)
const orderData = computed(() => store.getters['order/orderData'])
const resolvedOrder = computed(() => store.getters['order/resolvedOrder'])
const serviceOrderLoading = ref(false)

// the cell's active order, or (none left after a final resolution) its last resolved
// order — the list comes newest first
const serviceOrder = computed(() => {
  const cell = serviceCell.value
  if (!cell) return null
  const justResolved = resolvedOrder.value
  if (cell.active_order) {
    if (justResolved?.id === cell.active_order) return justResolved
    return manualOrders.value.find((o) => o.id === cell.active_order)
      || (orderData.value?.id === cell.active_order ? orderData.value : null)
  }
  const sameCell = (o) => String(o.cell_number) === String(cell.number) && o.resolution
  // resolvedOrder outlives the page, so match the device too (the order only names it)
  if (justResolved && sameCell(justResolved) && justResolved.device === locker.value.information.name) return justResolved
  return manualOrders.value.find(sameCell) || null
})

function loadManualOrders() {
  return store.dispatch('order/getManualOrderList', `?device=${route.params.slug}&manual_processing=true&page_size=100`)
}

// an active order beyond the first page of the manual list is fetched on its own
function ensureServiceOrder() {
  const id = serviceCell.value?.active_order
  if (!id || serviceOrder.value) return
  serviceOrderLoading.value = true
  store.dispatch('order/getOrder', id).finally(() => { serviceOrderLoading.value = false })
}

function openServicePanel(number) {
  serviceCellNumber.value = number
  ensureServiceOrder()
}

function onServiceChanged() {
  refreshCells().then(ensureServiceOrder)
  loadManualOrders()
  loadRecentOrders()
}

// ---- cell boards: replace-board resets a board, the device re-addresses the new one ----
// the saved status, not the header select: replace-board checks what the backend has
const inMaintenance = computed(() => instance.value?.status === 'maintenance')
const awaitingBoardCells = computed(() =>
  locker.value.cells_list.filter((c) => c && c.board_confirmed === false).map((c) => c.number))
const BOARD_POLL_MS = 12000
let boardPollTimer = null

// poll while a board waits for its address, to catch the confirmation and the new address
watch(() => awaitingBoardCells.value.length > 0, (waiting) => {
  if (waiting && !boardPollTimer) {
    boardPollTimer = setInterval(() => { if (!document.hidden) refreshCells() }, BOARD_POLL_MS)
  } else if (!waiting && boardPollTimer) {
    clearInterval(boardPollTimer)
    boardPollTimer = null
  }
})
// a hidden tab skips the polls, so catch up as soon as it is back
function onVisibilityChange() {
  if (!document.hidden && boardPollTimer) refreshCells()
}
document.addEventListener('visibilitychange', onVisibilityChange)
onUnmounted(() => {
  if (boardPollTimer) clearInterval(boardPollTimer)
  document.removeEventListener('visibilitychange', onVisibilityChange)
})

function switchToMaintenance() {
  updateInstance({ id: locker.value.information.sn, data: { status: 'maintenance' } })
    .then(() => {
      if (updateInstanceError.value) {
        errorNotify(updateInstanceError.value)
        return
      }
      locker.value.status = 'maintenance'
      successNotify('The locker is in maintenance mode')
      return refreshCells()
    })
}

function openCellModal(number) {
  const cell = locker.value.cells_list[number - 1]
  // cells are added by the device itself; an unregistered slot has nothing to edit
  if (!cell) return
  if (cell.status === 'out_of_service') {
    openServicePanel(number)
    return
  }
  editCellNumber.value = number
  showCellModal.value = true
}

// ---- copy helpers ----
const copiedKey = ref(null)
const controlUnitSn = computed(() => locker.value.control_unit?.unit?.sn || locker.value.control_unit?.sn || '')
function copyValue(key, text) {
  copyToClipBoard(text)
  copiedKey.value = key
  setTimeout(() => { copiedKey.value = null }, 1200)
}

function goBack() {
  router.push({ name: 'lockers' })
}

function instanceUpdate() {
  // the backend only takes the flags of existing cells (address etc. would be ignored)
  let cells_list = []
  locker.value.cells_list.forEach((element) => {
    if (!element) return
    cells_list.push({
      number: element.number,
      is_broken: element.is_broken,
      is_active: element.is_active,
      cleaning_required: element.cleaning_required,
    })
  });

  let data = {
    name: locker.value.information.name,
    organization: locker.value.organization.id,
    address: locker.value.address.id,
    model: locker.value.information.model.code,
    control_unit: locker.value.control_unit.sn,
    cells_list: cells_list,
    status: locker.value.status,
    ip_address: locker.value.information.ip_address,
    wifi_ssid: locker.value.information.wifi_ssid || null,
    cell_firmware_version: locker.value.information.cell_firmware_version.id,
    head_firmware_version: locker.value.control_unit.head_firmware_version.id
  }
  // write-only on the backend and a blank one would wipe it: sent only when typed in
  if (locker.value.information.wifi_password) data.wifi_password = locker.value.information.wifi_password
  updateInstance({ data: data, id: locker.value.information.sn })
    .then(() => {
      if (!updateInstanceError.value) { successNotify("Locker updated successfully!") }
      else { errorNotify(updateInstanceError.value) }
    })
    .finally(goBack)
}

function applyCells(cells) {
  cells.forEach((element) => {
    const local = dirtyCells.has(element.number) ? locker.value.cells_list[element.number - 1] : element
    locker.value.cells_list[element.number - 1] = {
      id: element.id,
      number: element.number,
      address: element.address,
      board_confirmed: element.board_confirmed,
      status: element.status,
      active_order: element.active_order,
      cleaning_required: local.cleaning_required,
      is_broken: local.is_broken,
      is_active: local.is_active,
    }
  });
}

// refetches the device but only takes its cells, so unsaved edits elsewhere on the page survive
function refreshCells() {
  return getInstance(route.params.slug)
    .then(() => {
      if (!getInstanceError.value) applyCells(instance.value.cells)
      else errorNotify(getInstanceError.value)
    })
}

function getInitialData() {
  dirtyCells.clear()
  const loader = $loading.show({});
  getInstance(route.params.slug)
    .then(() => {
      if (!getInstanceError.value) {
        locker.value.organization = instance.value.organization
        locker.value.information.sn = instance.value.sn
        locker.value.information.name = instance.value.name
        locker.value.information.model = instance.value.model
        locker.value.information.ip_address = instance.value.ip_address
        locker.value.information.wifi_ssid = instance.value.wifi_ssid || ''
        locker.value.information.wifi_password = ''
        locker.value.information.cell_firmware_version = instance.value.cell_firmware_version
        locker.value.control_unit.unit = instance.value.control_unit
        locker.value.control_unit.head_firmware_version = instance.value.head_firmware_version
        locker.value.address = instance.value.address
        locker.value.in_sync = instance.value.in_sync
        locker.value.battery_level = instance.value.battery_level
        locker.value.status = instance.value.status
        applyCells(instance.value.cells)
        setPageTitle(instance.value.name)
        setPageCrumbs(['Operations', 'Lockers', instance.value.name])
      }
      else { errorNotify(getInstanceError.value) }
    })
    .finally(() => { loader.hide() });
}

onMounted(() => {
  getInitialData()
  loadRecentOrders()
  loadManualOrders()
})
</script>

<template>
  <Teleport to="#topbar-actions">
    <button class="btn-ghost" type="button" @click="goBack">
      <span class="mdi mdi-arrow-left"></span>
      Back to Lockers
    </button>
  </Teleport>

  <!-- section-edit modals -->
  <OrganizationModal
      @close="showOrganizationModal=false"
      @setOrganization="setOrganization"
      v-model="showOrganizationModal"
      :instance="locker.organization"
      v-if="showOrganizationModal"/>
  <InformationModal
      @close="showInformationModal=false"
      @setInformation="setInformation"
      :instance="locker.information"
      v-model="showInformationModal"
      v-if="showInformationModal"/>
  <ControlUnitModal
      @close="showControlUnitModal=false"
      @setControlUnit="setControlUnit"
      :instance="locker.control_unit"
      v-model="showControlUnitModal"
      v-if="showControlUnitModal"/>
  <AddressModal
      @close="showAddressModal=false"
      @setAddress="setAddress"
      :instance="locker.address"
      v-model="showAddressModal"
      v-if="showAddressModal"/>
  <CellModal
      @close="showCellModal=false"
      @setCell="setCell"
      @changed="refreshCells"
      @maintenance="switchToMaintenance"
      :cell="editCell"
      :can-replace-board="isAdmin"
      :in-maintenance="inMaintenance"
      v-model="showCellModal"
      v-if="showCellModal && editCell"/>
  <CellServiceModal
      :open="serviceCellNumber !== null"
      :cell="serviceCell"
      :order="serviceOrder"
      :order-loading="serviceOrderLoading"
      :can-resolve="canResolveCells"
      :can-activate="isAdmin"
      :in-maintenance="inMaintenance"
      @maintenance="switchToMaintenance"
      @changed="onServiceChanged"
      @close="serviceCellNumber = null"/>

  <div class="lkd-top-row">
    <div class="metric-cluster">
      <div class="mc-total">
        <span class="mc-total__wm mdi mdi-view-grid-outline"></span>
        <div class="mc-total__title">Cells</div>
        <div class="mc-total__num">{{ totalCells }}</div>
      </div>
      <div v-for="m in cellMetrics" :key="m.label" class="mcard" :class="{ 'is-zero': m.value === 0 }">
        <div class="mcard__l">
          <div class="mcard__ico"><span class="mdi" :class="`mdi-${m.icon}`" :style="{ color: m.color }"></span></div>
          <div class="mcard__label">{{ m.label }}</div>
        </div>
        <div class="mcard__num">{{ m.value }}</div>
      </div>
    </div>

    <div class="card lkd-occ">
      <div class="card__head">
        <h2><span class="mdi mdi-chart-bar"></span> Cell Occupancy</h2>
        <div class="spacer"></div>
        <span class="sub">{{ occupancyToday }}% occupied today</span>
      </div>
      <div class="lkd-occ__bars">
        <div v-for="d in occupancyDemo" :key="d.day" class="lkd-occ__bar">
          <span class="lkd-occ__num">{{ d.value }}</span>
          <span
            class="lkd-occ__fill"
            :class="{ peak: d.value === occupancyMax }"
            :style="{ height: `${(d.value / occupancyMax) * 100}%` }"></span>
          <span class="lkd-occ__day">{{ d.day }}</span>
        </div>
      </div>
    </div>
  </div>

  <div class="lkd-detail">
    <div class="lkd-dhead">
      <div class="ic"><span class="mdi mdi-locker-multiple"></span></div>
      <div>
        <div class="kind">Locker · {{ locker.information.model?.name }}</div>
        <h2>{{ locker.information.name }}</h2>
      </div>
      <div class="lkd-statussel" :class="STATUS_SEL[locker.status] || 'st-offline'">
        <select v-model="locker.status">
          <option v-if="locker.status && !lockerStatuses.includes(locker.status)" :value="locker.status" disabled>{{ locker.status }}</option>
          <option v-for="s in lockerStatuses" :key="s" :value="s">{{ s }}</option>
        </select>
        <span class="mdi mdi-chevron-down caret"></span>
      </div>
      <div class="acts">
        <button class="btn-ghost" type="button" @click="goBack">Cancel</button>
        <button class="btn-primary" type="button" @click="instanceUpdate">
          <span class="mdi mdi-content-save-outline"></span> Save
        </button>
      </div>
    </div>

    <div v-if="awaitingBoardCells.length" class="lkd-boardwait">
      <span class="mdi mdi-timer-sand"></span>
      <span>
        Awaiting a new board: cell{{ awaitingBoardCells.length > 1 ? 's' : '' }}
        <b>{{ awaitingBoardCells.join(', ') }}</b>. Checking every {{ BOARD_POLL_MS / 1000 }} s.
      </span>
      <span class="mdi mdi-loading mdi-spin"></span>
    </div>
    <div class="lkd-dbody">
      <!-- cell map -->
      <div class="lkd-bp">
        <div class="lkd-ruler-h" :style="{ width: rulerWidth }"><span>{{ locker.information.model?.width }} mm</span></div>
        <div class="lkd-ruler-v"><span>{{ locker.information.model?.height }} mm</span></div>
        <div class="lkd-cmap">
          <div v-for="(mod, mi) in modules" :key="mi" class="lkd-cmodule">
            <div v-for="(rack, ri) in mod" :key="ri" class="lkd-crack">
              <button
                v-for="c in rack"
                :key="c.n"
                class="lkd-ccell"
                :class="[cellState(c.n), {
                  main: c.n === locker.information.model?.main_cell_number,
                  'board-wait': cellOf(c.n)?.board_confirmed === false,
                  unregistered: !cellOf(c.n),
                }]"
                :style="{ gridRow: `span ${c.span}` }"
                type="button"
                :title="cellTitle(c)"
                @click="openCellModal(c.n)">
                <span v-if="cellOf(c.n)?.board_confirmed === false" class="cflag mdi mdi-chip"></span>
                <span v-else-if="cellOf(c.n)?.cleaning_required" class="cflag mdi mdi-broom"></span>
                <span v-else-if="cellOf(c.n)?.active_order && cellState(c.n) === 'st-oos'" class="cflag mdi mdi-package-variant-closed"></span>
                <span v-else-if="['occupied', 'rented'].includes(cellOf(c.n)?.status)" class="cflag mdi mdi-package-variant-closed"></span>
                <span class="cn">{{ c.n }}</span>
                <span v-if="cellState(c.n) === 'st-oos'" class="oosbadge">Down</span>
                <span v-else class="cs">{{ c.size }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="lkd-legend">
          <span v-for="[bg, label] in LEGEND" :key="label" class="lg">
            <span class="sw" :style="{ background: bg }"></span>{{ label }}
          </span>
          <span class="lg"><span class="mdi mdi-broom" style="color: var(--lb-warning);"></span>Cleaning</span>
          <span class="lg"><span class="lg-star">&#9733;</span>Main cell</span>
          <span class="lg"><span class="mdi mdi-chip" style="color: var(--fg-2);"></span>Awaiting new board</span>
        </div>
      </div>

      <!-- details -->
      <div class="lkd-info">
        <dl class="lkd-dl">
          <dt>Serial number</dt>
          <dd>
            <span class="copyd">
              {{ locker.information.sn }}
              <button
                class="lkd-copybtn"
                :class="{ done: copiedKey === 'sn' }"
                type="button"
                title="Copy S/N"
                @click="copyValue('sn', locker.information.sn)">
                <span class="mdi" :class="copiedKey === 'sn' ? 'mdi-check' : 'mdi-content-copy'"></span>
              </button>
            </span>
          </dd>
          <dt>Model</dt>
          <dd>{{ locker.information.model?.name }} ({{ locker.information.model?.code }})</dd>
          <dt>Organization</dt>
          <dd>
            <span class="copyd">
              {{ locker.organization?.name || '—' }}
              <button class="lkd-copybtn" type="button" title="Edit organization" @click="showOrganizationModal = true">
                <span class="mdi mdi-pencil-outline"></span>
              </button>
            </span>
          </dd>
          <dt>Address</dt>
          <dd>
            <span class="copyd">
              {{ [locker.address?.line1, locker.address?.city?.name].filter(Boolean).join(', ') || '—' }}
              <button class="lkd-copybtn" type="button" title="Edit address" @click="showAddressModal = true">
                <span class="mdi mdi-pencil-outline"></span>
              </button>
            </span>
          </dd>
          <dt>IP address</dt>
          <dd>
            <span class="copyd">
              <span class="mono">{{ locker.information.ip_address || '—' }}</span>
              <button class="lkd-copybtn" type="button" title="Edit locker information" @click="showInformationModal = true">
                <span class="mdi mdi-pencil-outline"></span>
              </button>
            </span>
          </dd>
          <dt>Wi-Fi SSID</dt>
          <dd>
            <span class="copyd">
              <span class="mono">{{ locker.information.wifi_ssid || '—' }}</span>
              <button class="lkd-copybtn" type="button" title="Edit Wi-Fi" @click="showInformationModal = true">
                <span class="mdi mdi-pencil-outline"></span>
              </button>
            </span>
          </dd>
          <dt>Control unit S/N</dt>
          <dd>
            <span class="copyd">
              <span class="mono">{{ controlUnitSn || '—' }}</span>
              <button
                v-if="controlUnitSn"
                class="lkd-copybtn"
                :class="{ done: copiedKey === 'cu' }"
                type="button"
                title="Copy control unit S/N"
                @click="copyValue('cu', controlUnitSn)">
                <span class="mdi" :class="copiedKey === 'cu' ? 'mdi-check' : 'mdi-content-copy'"></span>
              </button>
              <button class="lkd-copybtn" type="button" title="Edit control unit" @click="showControlUnitModal = true">
                <span class="mdi mdi-pencil-outline"></span>
              </button>
            </span>
          </dd>
          <dt>CU firmware</dt>
          <dd>{{ locker.control_unit.head_firmware_version?.version || '—' }}</dd>
          <dt>Cells firmware</dt>
          <dd>{{ locker.information.cell_firmware_version?.version || '—' }}</dd>
          <dt>Sync</dt>
          <dd>
            <span v-if="locker.in_sync" style="color: var(--lb-success);">
              <span class="mdi mdi-access-point"></span> Synced
            </span>
            <span v-else style="color: var(--fg-2);">
              <span class="mdi mdi-access-point-off"></span> Out of sync
            </span>
          </dd>
          <dt>Battery</dt>
          <dd>
            <span class="lkd-battery">
              <span class="mdi mdi-battery-outline" style="color: var(--fg-2);"></span>
              {{ locker.in_sync && locker.battery_level ? `${locker.battery_level}%` : 'N/A' }}
            </span>
          </dd>
        </dl>
      </div>
    </div>
  </div>

  <div v-if="unresolvedOrders.length" class="card">
    <div class="card__head">
      <span class="mdi mdi-alert-octagon-outline" style="font-size: 19px; color: var(--lb-red);"></span>
      <div>
        <h2>Needs resolution</h2>
        <div class="sub">Orders left in out of service cells — resolve the content before returning a cell to service</div>
      </div>
      <div class="spacer"></div>
      <span class="count">{{ unresolvedOrders.length }}</span>
    </div>
    <DataTable
      :cols="manualCols"
      :columns="manualColumns"
      :rows="unresolvedOrders">
      <template #row="{ row }">
        <div class="td">
          <button class="linkcell" type="button" title="Open order" @click="goToOrder(row)">
            <span class="mdi mdi-clipboard-text-outline"></span>
            <span class="mono strong">#{{ row.id }}</span>
          </button>
        </div>
        <div class="td"><span class="mono">{{ row.cell_number ?? '—' }}</span></div>
        <div class="td"><span class="muted">{{ row.locked_at ? prettifyDateTime(row.locked_at) : (row.created_at ? prettifyDateTime(row.created_at) : '—') }}</span></div>
        <div class="td">
          <button class="linkcell" type="button" title="Open the cell panel" @click="openServicePanel(Number(row.cell_number))">
            <span class="mdi mdi-wrench-outline"></span> Resolve
          </button>
        </div>
      </template>
    </DataTable>
  </div>

  <div class="card">
    <div class="card__head">
      <span class="mdi mdi-history" style="font-size: 19px; color: var(--lb-blue);"></span>
      <div>
        <h2>Recent Activity</h2>
        <div class="sub">Rental orders on this locker</div>
      </div>
      <div class="spacer"></div>
      <span class="count">{{ orderList?.count ?? recentOrders.length }}</span>
    </div>
    <DataTable
      :cols="orderCols"
      :columns="orderColumns"
      :rows="recentOrders"
      empty-text="No rental activity recorded for this locker yet.">
      <template #row="{ row }">
        <div class="td">
          <button class="linkcell" type="button" title="Open order" @click="goToOrder(row)">
            <span class="mdi mdi-clipboard-text-outline"></span>
            <span class="mono strong">#{{ row.id }}</span>
          </button>
        </div>
        <div class="td"><span class="mono">{{ row.cell_number ?? '—' }}</span></div>
        <div class="td">
          <span class="pill" :class="`pill--${(ORDER_STATUS_META[row.status] || ORDER_STATUS_META.created).pill}`">
            <span class="d"></span>
            {{ (ORDER_STATUS_META[row.status] || { label: row.status }).label }}
          </span>
        </div>
        <div class="td"><span class="muted">{{ row.created_at ? prettifyDateTime(row.created_at) : '—' }}</span></div>
        <div class="td"><span class="muted">{{ row.expired_at ? prettifyDateTime(row.expired_at) : '—' }}</span></div>
      </template>
    </DataTable>
  </div>
</template>
